import { Checkbox, Form } from 'tio-ui/components/forms';
import type { FormResultData } from 'tio-ui/components/forms';
import { or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type { PaymentFilterSignature as S } from '../types.gts';

// Form data structure for payment states and year
// Includes both checkbox states and the selected payment year
interface PaymentStatesFormData {
  [key: string]: boolean | string | null | undefined;
}

function getYearsFormatted(numberOfYears: number, monthOffset?: number | null) {
  const formattedYears = [];
  const now = new Date();
  let startYear = now.getFullYear();

  // Check if the current month is less than the provided month offset
  // Remember, getMonth() returns 0 for January, 1 for February, etc.
  const shouldIncrementYear =
    monthOffset !== undefined && monthOffset !== null && now.getMonth() + 1 >= monthOffset;
  if (shouldIncrementYear) {
    startYear += 1; // Start from the next year if the current month is before the offset
  }

  for (let i = 0; i < numberOfYears; i++) {
    formattedYears.push(`${startYear - i}`);
  }

  return formattedYears;
}

interface PaymentFilterSignature {
  Args: S & {
    paymentYear?: string;
    monthOffset?: number;
    onFilterUpdate: (filterUpdates: Record<string, unknown>) => void;
  };
}

export default class PaymentFilter extends Component<PaymentFilterSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  @tracked paymentStatesFormData: PaymentStatesFormData = { paymentYear: '' };

  get initializedFormData(): PaymentStatesFormData {
    // Build the form data with checkbox states from @paymentStates prop
    const formData: PaymentStatesFormData = {
      ...this.paymentStatesFormData,
      paymentYear: this.args.paymentYear || '',
    };

    // If @paymentStates is provided, ensure those checkboxes are marked as true
    if (this.args.paymentStates) {
      const selectedStates = this.args.paymentStates.split(',').filter(Boolean);
      for (const state of selectedStates) {
        formData[state] = true;
      }
    }

    return formData;
  }

  get paymentStates(): { key: string; label: string }[] {
    return [
      {
        key: 'tas_application_payment_final_approved_total_amount_integer',
        label: 'Approved Total',
      },
      { key: 'tas_application_payment_pending_amount_integer', label: 'Pending Amount' },
      { key: 'tas_application_payment_paid_amount_integer', label: 'Paid Amount' },
      { key: 'tas_application_payment_last_date_date', label: 'Payment Date' },
      {
        key: 'tas_application_payment_refund_requested_amount_integer',
        label: 'Refund Requested Amount',
      },
      {
        key: 'tas_application_payment_refund_paid_back_amount_integer',
        label: 'Refund Paid Back Amount',
      },
      {
        key: 'tas_application_payment_refund_forgiven_amount_integer',
        label: 'Refund Forgiven Amount',
      },
      {
        key: 'tas_application_payment_refund_cancelled_amount_integer',
        label: 'Refund Cancelled Amount',
      },
      { key: 'tas_application_payment_refund_limit_amount_integer', label: 'Refund Limit Amount' },
    ];
  }

  includePaymentsDidChange = (state: boolean) => {
    this.args.onFilterUpdate({ includePayments: state, paymentStates: '', paymentYear: '' });
  };

  selectedPaymentStatesDidChange = (result: FormResultData<unknown>, _event: Event) => {
    const data = result.data as Record<string, unknown>;
    this.paymentStatesFormData = data as PaymentStatesFormData;

    const selectedStates: string[] = [];
    let paymentYear = '';

    for (const [key, value] of Object.entries(data)) {
      if (key === 'paymentYear') {
        // Handle the select value
        paymentYear = (value as string) || '';
      } else if (value === true) {
        // Collect selected checkbox states
        selectedStates.push(key);
      }
    }

    const paymentStates = selectedStates.join(',');
    this.args.onFilterUpdate({ paymentStates, paymentYear });
  };

  selectedPaymentStatesDidSubmit = () => {
    // No-op for form submission - filters update via onChange
  };

  <template>
    <Checkbox
      @label={{t "common.reporting.include_payment_details"}}
      @checked={{or @includePayments false}}
      @onChange={{this.includePaymentsDidChange}}
    />

    {{#if @includePayments}}
      <div class="ml-4 flex flex-col gap-y-3">
        <Form
          @data={{this.initializedFormData}}
          @onChange={{this.selectedPaymentStatesDidChange}}
          @onSubmit={{this.selectedPaymentStatesDidSubmit}}
          class="flex flex-col gap-y-3"
          as |form|
        >
          {{#each this.paymentStates as |state|}}
            <form.Field @name={{state.key}} as |field|>
              <field.Checkbox @label={{state.label}} @size="sm" />
            </form.Field>
          {{/each}}

          <form.Field @name="paymentYear" as |field|>
            <field.SingleSelect
              @label={{t "common.reporting.payment_fiscal_year"}}
              @items={{getYearsFormatted 5 @monthOffset}}
              @allowEmpty={{true}}
              @isClearable={{true}}
              @placeholder="Select year"
            />
          </form.Field>
        </Form>
      </div>
    {{/if}}
  </template>
}
